import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
} from "@chakra-ui/react";
import Cropper, { Area } from "react-easy-crop";
import { cropImage } from "./cropImage";

export interface ImageCropModalProps {
  /** Source file picked from the file input. */
  file: File | null;
  isOpen: boolean;
  onClose: () => void;
  /** Receives the cropped File ready for upload. The consumer awaits this; the modal stays in `isPending` until it resolves. */
  onConfirm: (croppedFile: File) => Promise<void> | void;
  /** Crop aspect ratio (width / height). e.g. 1 = square, 16/9, 2/3. */
  aspect: number;
  /** "round" for circular avatars, "rect" for everything else. */
  cropShape?: "round" | "rect";
  /** Output mime type for the cropped file. Default image/png. */
  outputMime?: string;
  /** Quality 0..1 for lossy outputs. */
  quality?: number;
  /** Modal heading text. */
  title: string;
  /** Confirm button label. Default "Save". */
  confirmLabel?: string;
  /** Cancel button label. Default "Cancel". */
  cancelLabel?: string;
}

export function ImageCropModal({
  file,
  isOpen,
  onClose,
  onConfirm,
  aspect,
  cropShape = "rect",
  outputMime,
  quality,
  title,
  confirmLabel = "Save",
  cancelLabel = "Cancel",
}: ImageCropModalProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const imageUrl = useMemo(() => {
    if (!file) return null;
    return URL.createObjectURL(file);
  }, [file]);

  useEffect(() => {
    return () => {
      if (imageUrl) URL.revokeObjectURL(imageUrl);
    };
  }, [imageUrl]);

  useEffect(() => {
    if (!isOpen) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setIsSaving(false);
    }
  }, [isOpen]);

  if (!isOpen || !file || !imageUrl) return null;

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    setIsSaving(true);
    try {
      const cropped = await cropImage({
        imageSrc: imageUrl,
        cropArea: croppedAreaPixels,
        originalFileName: file.name,
        outputMime,
        quality,
      });
      await onConfirm(cropped);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnEsc={!isSaving}
      closeOnOverlayClick={!isSaving}
      isCentered
      size="lg"
    >
      <ModalOverlay
        bg="rgba(0, 0, 0, 0.72)"
        sx={{ backdropFilter: "blur(18px)" }}
      />
      <ModalContent
        bg="#141517f2"
        border="1px solid"
        borderColor="transparent.white.10"
        borderRadius="12px"
        color="dune.dune"
      >
        <ModalHeader
          fontSize="xl"
          fontWeight={700}
          letterSpacing="0.04em"
          textTransform="uppercase"
        >
          {title}
        </ModalHeader>

        <ModalBody>
          <Box bg="neutral.900" height="360px" position="relative">
            <Cropper
              aspect={aspect}
              crop={crop}
              cropShape={cropShape}
              image={imageUrl}
              objectFit="contain"
              onCropChange={setCrop}
              onCropComplete={(_a, next) => setCroppedAreaPixels(next)}
              onZoomChange={setZoom}
              showGrid={false}
              zoom={zoom}
            />
          </Box>

          <Box px={4} py={5}>
            <Slider
              aria-label="Image zoom"
              max={3}
              min={1}
              onChange={setZoom}
              step={0.01}
              value={zoom}
            >
              <SliderTrack bg="transparent.white.20">
                <SliderFilledTrack bg="transparent.white.50" />
              </SliderTrack>
              <SliderThumb />
            </Slider>
          </Box>
        </ModalBody>

        <ModalFooter>
          <Flex gap={3}>
            <Button
              colorScheme="orange"
              isLoading={isSaving}
              onClick={handleConfirm}
            >
              {confirmLabel}
            </Button>
            <Button
              onClick={onClose}
              isDisabled={isSaving}
              bg="transparent"
              borderColor="transparent.white.20"
              color="dune.dune"
              variant="outline"
              _active={{
                bg: "transparent.white.10",
                borderColor: "transparent.white.30",
              }}
              _hover={{
                bg: "transparent.white.10",
                borderColor: "transparent.white.30",
              }}
            >
              {cancelLabel}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
